import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch, useParams } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import { FloatingPhone } from "@/components/FloatingPhone";

// ─── Page loading fallback ────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white" role="status" aria-label="Loading page">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-[#c9a84c] border-t-transparent rounded-full animate-spin" aria-hidden="true" />
        <span className="text-sm text-gray-500 font-medium">Loading…</span>
      </div>
    </div>
  );
}

// ─── Core pages (eager — always needed) ──────────────────────────────────────
import Home from "./pages/Home";
import NotFound from "@/pages/NotFound";

// ─── Lazy-loaded pages ────────────────────────────────────────────────────────
const GetStartedPage = lazy(() => import("./pages/GetStartedPage"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Services = lazy(() => import("./pages/Services"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Industries = lazy(() => import("./pages/Industries"));
const Locations = lazy(() => import("./pages/Locations"));
const Blog = lazy(() => import("./pages/Blog"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const SEOTraining = lazy(() => import("./pages/SEOTraining"));
const ServiceDetailPage = lazy(() => import("./pages/ServiceDetail"));
const IndustryDetailPage = lazy(() => import("./pages/IndustryDetail"));
const CityDetailPage = lazy(() => import("./pages/CityDetail"));
const Admin = lazy(() => import("./pages/Admin"));
const CRMDashboard = lazy(() => import("./pages/crm/CRMDashboard"));
const CRMLeads = lazy(() => import("./pages/crm/CRMLeads"));
const CRMPipeline = lazy(() => import("./pages/crm/CRMPipeline"));
const CRMContacts = lazy(() => import("./pages/crm/CRMContacts"));
const CRMTasks = lazy(() => import("./pages/crm/CRMTasks"));
const CRMContent = lazy(() => import("./pages/crm/CRMContent"));
const CRMGuide = lazy(() => import("./pages/crm/CRMGuide"));
const CMSBlog = lazy(() => import("./pages/crm/CMSBlog"));
const CMSTestimonials = lazy(() => import("./pages/crm/CMSTestimonials"));
const CMSCaseStudies = lazy(() => import("./pages/crm/CMSCaseStudies"));
const CMSPages = lazy(() => import("./pages/crm/CMSPages"));
const CRMMetaTags = lazy(() => import("./pages/crm/CRMMetaTags"));
const CRMPageManager = lazy(() => import("./pages/crm/CRMPageManager"));
const DynamicPage = lazy(() => import("./pages/DynamicPage"));
const CRMLeadDetail = lazy(() => import("./pages/crm/CRMLeadDetail"));
const CRMContactDetail = lazy(() => import("./pages/crm/CRMContactDetail"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPost"));

// ─── Service pages ────────────────────────────────────────────────────────────
const AEOAgencyPage = lazy(() => import("./pages/AEOAgencyPage"));
const AISearchOptimizationPage = lazy(() => import("./pages/AISearchOptimizationPage"));
const GEOPage = lazy(() => import("./pages/GEOPage"));
const ChatGPTSEOPage = lazy(() => import("./pages/ChatGPTSEOPage"));
const ClaudeSEOPage = lazy(() => import("./pages/ClaudeSEOPage"));
const GeminiSEOPage = lazy(() => import("./pages/GeminiSEOPage"));
const PerplexitySEOPage = lazy(() => import("./pages/PerplexitySEOPage"));
const LLMSEOPage = lazy(() => import("./pages/LLMSEOPage"));
const GoogleBusinessProfilePage = lazy(() => import("./pages/GoogleBusinessProfilePage"));
const LinkBuildingPage = lazy(() => import("./pages/LinkBuildingPage"));
const LocalSEOPage = lazy(() => import("./pages/LocalSEOPage"));
const OnPageSEOPage = lazy(() => import("./pages/OnPageSEOPage"));
const PPCManagementPage = lazy(() => import("./pages/PPCManagementPage"));
const SemanticSEOPage = lazy(() => import("./pages/SemanticSEOPage"));
const SocialMediaAdsPage = lazy(() => import("./pages/SocialMediaAdsPage"));
const ContentMarketingPage = lazy(() => import("./pages/ContentMarketingPage"));
const ReputationManagementPage = lazy(() => import("./pages/ReputationManagementPage"));
const WebsiteDesignPage = lazy(() => import("./pages/WebsiteDesignPage"));

// ─── Industry pages ───────────────────────────────────────────────────────────
const HealthcarePage = lazy(() => import("./pages/HealthcarePage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const RealEstatePage = lazy(() => import("./pages/RealEstatePage"));
const RestaurantsPage = lazy(() => import("./pages/RestaurantsPage"));
const HomeServicesPage = lazy(() => import("./pages/HomeServicesPage"));
const HVACPage = lazy(() => import("./pages/HVACPage"));
const RoofingPage = lazy(() => import("./pages/RoofingPage"));
const ElectriciansPage = lazy(() => import("./pages/ElectriciansPage"));
const GeneralContractorsPage = lazy(() => import("./pages/GeneralContractorsPage"));
const PlumbersPage = lazy(() => import("./pages/PlumbersPage"));
const AutomotivePage = lazy(() => import("./pages/AutomotivePage"));

const DynamicCityPage = lazy(() => import("./pages/DynamicCityPage"));
// ─── State hub pages ─────────────────────────────────────────────────────────
const AlabamaPage = lazy(() => import("./pages/AlabamaPage"));
const AlaskaPage = lazy(() => import("./pages/AlaskaPage"));
const ArizonaPage = lazy(() => import("./pages/ArizonaPage"));
const ArkansasPage = lazy(() => import("./pages/ArkansasPage"));
const AtlantaPage = lazy(() => import("./pages/AtlantaPage"));
const AustinPage = lazy(() => import("./pages/AustinPage"));
const CaliforniaPage = lazy(() => import("./pages/CaliforniaPage"));
const CharlottePage = lazy(() => import("./pages/CharlottePage"));
const ColoradoPage = lazy(() => import("./pages/ColoradoPage"));
const ConnecticutPage = lazy(() => import("./pages/ConnecticutPage"));
const DallasPage = lazy(() => import("./pages/DallasPage"));
const DelawarePage = lazy(() => import("./pages/DelawarePage"));
const FloridaPage = lazy(() => import("./pages/FloridaPage"));
const FortLauderdalePage = lazy(() => import("./pages/FortLauderdalePage"));
const GainesvillePage = lazy(() => import("./pages/GainesvillePage"));
// ─── Florida city pages ───────────────────────────────────────────────────────
const GeorgiaPage = lazy(() => import("./pages/GeorgiaPage"));
const HawaiiPage = lazy(() => import("./pages/HawaiiPage"));
const IdahoPage = lazy(() => import("./pages/IdahoPage"));
const IllinoisPage = lazy(() => import("./pages/IllinoisPage"));
const IndianaPage = lazy(() => import("./pages/IndianaPage"));
const IowaPage = lazy(() => import("./pages/IowaPage"));
const KansasPage = lazy(() => import("./pages/KansasPage"));
const KentuckyPage = lazy(() => import("./pages/KentuckyPage"));
const LouisianaPage = lazy(() => import("./pages/LouisianaPage"));
const MainePage = lazy(() => import("./pages/MainePage"));
const MarylandPage = lazy(() => import("./pages/MarylandPage"));
const MassachusettsPage = lazy(() => import("./pages/MassachusettsPage"));
const MemphisPage = lazy(() => import("./pages/MemphisPage"));
const MichiganPage = lazy(() => import("./pages/MichiganPage"));
const MinnesotaPage = lazy(() => import("./pages/MinnesotaPage"));
const MississippiPage = lazy(() => import("./pages/MississippiPage"));
const MissouriPage = lazy(() => import("./pages/MissouriPage"));
const MontanaPage = lazy(() => import("./pages/MontanaPage"));
const NaplesPage = lazy(() => import("./pages/NaplesPage"));
const NebraskaPage = lazy(() => import("./pages/NebraskaPage"));
const NevadaPage = lazy(() => import("./pages/NevadaPage"));
const NewHampshirePage = lazy(() => import("./pages/NewHampshirePage"));
const NewJerseyPage = lazy(() => import("./pages/NewJerseyPage"));
const NewMexicoPage = lazy(() => import("./pages/NewMexicoPage"));
const NewYorkPage = lazy(() => import("./pages/NewYorkPage"));
const NorthCarolinaPage = lazy(() => import("./pages/NorthCarolinaPage"));
const NorthDakotaPage = lazy(() => import("./pages/NorthDakotaPage"));
const OcalaPage = lazy(() => import("./pages/OcalaPage"));
const OhioPage = lazy(() => import("./pages/OhioPage"));
const OklahomaPage = lazy(() => import("./pages/OklahomaPage"));
const OmahaPage = lazy(() => import("./pages/OmahaPage"));
const OregonPage = lazy(() => import("./pages/OregonPage"));
const PacificNorthwestPage = lazy(() => import("./pages/PacificNorthwestPage"));
const PennsylvaniaPage = lazy(() => import("./pages/PennsylvaniaPage"));
const PhoenixPage = lazy(() => import("./pages/PhoenixPage"));
const RhodeIslandPage = lazy(() => import("./pages/RhodeIslandPage"));
const SanFranciscoPage = lazy(() => import("./pages/SanFranciscoPage"));
const SouthCarolinaPage = lazy(() => import("./pages/SouthCarolinaPage"));
const SouthDakotaPage = lazy(() => import("./pages/SouthDakotaPage"));
const TennesseePage = lazy(() => import("./pages/TennesseePage"));
const TexasPage = lazy(() => import("./pages/TexasPage"));
const UtahPage = lazy(() => import("./pages/UtahPage"));
const VermontPage = lazy(() => import("./pages/VermontPage"));
const VirginiaPage = lazy(() => import("./pages/VirginiaPage"));
const WashingtonDCPage = lazy(() => import("./pages/WashingtonDCPage"));
const WashingtonPage = lazy(() => import("./pages/WashingtonPage"));
const WestVirginiaPage = lazy(() => import("./pages/WestVirginiaPage"));
const WisconsinPage = lazy(() => import("./pages/WisconsinPage"));
const WyomingPage = lazy(() => import("./pages/WyomingPage"));
// ─── Wrapper components ───────────────────────────────────────────────────────
function ServiceDetailWrapper() {
  const params = useParams<{ slug: string }>();
  return <ServiceDetailPage slug={params.slug || ""} />;
}
function IndustryDetailWrapper() {
  const params = useParams<{ slug: string }>();
  return <IndustryDetailPage slug={params.slug || ""} />;
}
function CityDetailWrapper() {
  const params = useParams<{ slug: string }>();
  return <CityDetailPage slug={params.slug || ""} />;
}

// ─── Router ───────────────────────────────────────────────────────────────────
function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/about" component={About} />
        <Route path="/contact" component={Contact} />
        <Route path="/get-started" component={GetStartedPage} />
        <Route path="/services" component={Services} />
        <Route path="/pricing" component={Pricing} />
        <Route path="/services/aeo-agency" component={AEOAgencyPage} />
        <Route path="/services/ai-search-optimization" component={AISearchOptimizationPage} />
        <Route path="/services/aeo-agency/aio-seo" component={AISearchOptimizationPage} />
        <Route path="/services/generative-engine-optimization" component={GEOPage} />
        <Route path="/services/aeo-agency/geo-seo" component={GEOPage} />
        <Route path="/services/aeo-agency/chatgpt-seo" component={ChatGPTSEOPage} />
        <Route path="/services/aeo-agency/claude-seo" component={ClaudeSEOPage} />
        <Route path="/services/aeo-agency/gemini-seo" component={GeminiSEOPage} />
        <Route path="/services/aeo-agency/perplexity-seo" component={PerplexitySEOPage} />
        <Route path="/services/aeo-agency/llm-seo" component={LLMSEOPage} />
        <Route path="/services/google-business-profile" component={GoogleBusinessProfilePage} />
        <Route path="/services/link-building" component={LinkBuildingPage} />
        <Route path="/services/local-seo" component={LocalSEOPage} />
        <Route path="/services/on-page-seo" component={OnPageSEOPage} />
        <Route path="/services/ppc-management" component={PPCManagementPage} />
        <Route path="/services/semantic-seo" component={SemanticSEOPage} />
        <Route path="/services/social-media-ads" component={SocialMediaAdsPage} />
        <Route path="/services/content-marketing" component={ContentMarketingPage} />
        <Route path="/services/reputation-management" component={ReputationManagementPage} />
        <Route path="/services/website-design" component={WebsiteDesignPage} />
        <Route path="/services/:slug" component={ServiceDetailWrapper} />
        <Route path="/industries" component={Industries} />
        {/* Canonical -seo URLs (primary) */}
        <Route path="/industries/healthcare-seo" component={HealthcarePage} />
        <Route path="/industries/legal-seo" component={LegalPage} />
        <Route path="/industries/real-estate-seo" component={RealEstatePage} />
        <Route path="/industries/restaurants-seo" component={RestaurantsPage} />
        <Route path="/industries/home-services-seo" component={HomeServicesPage} />
        <Route path="/industries/hvac-seo" component={HVACPage} />
        <Route path="/industries/roofing-seo" component={RoofingPage} />
        <Route path="/industries/electricians-seo" component={ElectriciansPage} />
        <Route path="/industries/general-contractors-seo" component={GeneralContractorsPage} />
        <Route path="/industries/plumbing-seo" component={PlumbersPage} />
        <Route path="/industries/automotive-seo" component={AutomotivePage} />
        {/* Legacy aliases - old URLs still work */}
        <Route path="/industries/healthcare" component={HealthcarePage} />
        <Route path="/industries/legal" component={LegalPage} />
        <Route path="/industries/real-estate" component={RealEstatePage} />
        <Route path="/industries/restaurants" component={RestaurantsPage} />
        <Route path="/industries/home-services" component={HomeServicesPage} />
        <Route path="/industries/hvac" component={HVACPage} />
        <Route path="/industries/roofing" component={RoofingPage} />
        <Route path="/industries/electricians" component={ElectriciansPage} />
        <Route path="/industries/general-contractors" component={GeneralContractorsPage} />
        <Route path="/industries/plumbing" component={PlumbersPage} />
        <Route path="/industries/automotive" component={AutomotivePage} />
        <Route path="/industries/:slug" component={IndustryDetailWrapper} />
        <Route path="/locations" component={Locations} />
        {/* Georgia */}
        {/* State hub pages */}
        <Route path="/locations/atlanta-ga" component={AtlantaPage} />
        <Route path="/locations/georgia" component={GeorgiaPage} />
        <Route path="/locations/louisiana" component={LouisianaPage} />
        <Route path="/locations/texas" component={TexasPage} />
        <Route path="/locations/austin-tx" component={AustinPage} />
        <Route path="/locations/dallas-tx" component={DallasPage} />
        <Route path="/locations/florida" component={FloridaPage} />
        <Route path="/locations/naples-fl" component={NaplesPage} />
        <Route path="/locations/ocala-fl" component={OcalaPage} />
        <Route path="/locations/fort-lauderdale-fl" component={FortLauderdalePage} />
        <Route path="/locations/gainesville-fl" component={GainesvillePage} />
        <Route path="/locations/indiana" component={IndianaPage} />
        <Route path="/locations/ohio" component={OhioPage} />
        <Route path="/locations/illinois" component={IllinoisPage} />
        <Route path="/locations/nevada" component={NevadaPage} />
        <Route path="/locations/pennsylvania" component={PennsylvaniaPage} />
        <Route path="/locations/alabama" component={AlabamaPage} />
        <Route path="/locations/virginia" component={VirginiaPage} />
        <Route path="/locations/colorado" component={ColoradoPage} />
        <Route path="/locations/michigan" component={MichiganPage} />
        <Route path="/locations/arizona" component={ArizonaPage} />
        <Route path="/locations/phoenix-az" component={PhoenixPage} />
        <Route path="/locations/san-francisco-ca" component={SanFranciscoPage} />
        <Route path="/locations/washington-dc" component={WashingtonDCPage} />
        <Route path="/locations/omaha-ne" component={OmahaPage} />
        <Route path="/locations/memphis-tn" component={MemphisPage} />
        <Route path="/locations/charlotte-nc" component={CharlottePage} />
        <Route path="/locations/washington" component={WashingtonPage} />
        <Route path="/locations/oregon" component={OregonPage} />
        <Route path="/locations/pacific-northwest" component={PacificNorthwestPage} />
        <Route path="/locations/idaho" component={IdahoPage} />
        <Route path="/locations/north-carolina" component={NorthCarolinaPage} />
        <Route path="/locations/tennessee" component={TennesseePage} />
        <Route path="/locations/nebraska" component={NebraskaPage} />
        <Route path="/locations/south-carolina" component={SouthCarolinaPage} />
        <Route path="/locations/alaska" component={AlaskaPage} />
        <Route path="/locations/arkansas" component={ArkansasPage} />
        <Route path="/locations/connecticut" component={ConnecticutPage} />
        <Route path="/locations/hawaii" component={HawaiiPage} />
        <Route path="/locations/iowa" component={IowaPage} />
        <Route path="/locations/kansas" component={KansasPage} />
        <Route path="/locations/maine" component={MainePage} />
        <Route path="/locations/massachusetts" component={MassachusettsPage} />
        <Route path="/locations/minnesota" component={MinnesotaPage} />
        <Route path="/locations/mississippi" component={MississippiPage} />
        <Route path="/locations/missouri" component={MissouriPage} />
        <Route path="/locations/montana" component={MontanaPage} />
        <Route path="/locations/new-hampshire" component={NewHampshirePage} />
        <Route path="/locations/north-dakota" component={NorthDakotaPage} />
        <Route path="/locations/oklahoma" component={OklahomaPage} />
        <Route path="/locations/rhode-island" component={RhodeIslandPage} />
        <Route path="/locations/south-dakota" component={SouthDakotaPage} />
        <Route path="/locations/vermont" component={VermontPage} />
        <Route path="/locations/wyoming" component={WyomingPage} />
          <Route path="/locations/california" component={CaliforniaPage} />
          <Route path="/locations/delaware" component={DelawarePage} />
          <Route path="/locations/kentucky" component={KentuckyPage} />
          <Route path="/locations/maryland" component={MarylandPage} />
          <Route path="/locations/new-jersey" component={NewJerseyPage} />
          <Route path="/locations/new-mexico" component={NewMexicoPage} />
          <Route path="/locations/new-york" component={NewYorkPage} />
          <Route path="/locations/utah" component={UtahPage} />
          <Route path="/locations/west-virginia" component={WestVirginiaPage} />
          <Route path="/locations/wisconsin" component={WisconsinPage} />
        {/* Dynamic city pages - single route replaces 933 individual routes */}
        <Route path="/locations/:stateSlug/:citySlug" component={DynamicCityPage} />
        <Route path="/blog" component={Blog} />
        <Route path="/blog/:slug">{(params) => <BlogPostPage slug={params.slug || ""} />}</Route>
        <Route path="/case-studies" component={CaseStudies} />
        <Route path="/seo-training" component={SEOTraining} />
        <Route path="/admin" component={Admin} />
        {/* ─── CRM routes ──────────────────────────────────────────────── */}
        <Route path="/crm" component={CRMDashboard} />
        <Route path="/crm/leads" component={CRMLeads} />
        <Route path="/crm/pipeline" component={CRMPipeline} />
        <Route path="/crm/contacts" component={CRMContacts} />
        <Route path="/crm/tasks" component={CRMTasks} />
        <Route path="/crm/guide" component={CRMGuide} />
        <Route path="/crm/leads/:id" component={CRMLeadDetail} />
        <Route path="/crm/contacts/:id" component={CRMContactDetail} />
        <Route path="/crm/content" component={CRMContent} />
        <Route path="/crm/cms/blog" component={CMSBlog} />
        <Route path="/crm/cms/testimonials" component={CMSTestimonials} />
        <Route path="/crm/cms/case-studies" component={CMSCaseStudies} />
        <Route path="/crm/cms/pages" component={CMSPages} />
        <Route path="/crm/meta" component={CRMMetaTags} />
        <Route path="/crm/pages" component={CRMPageManager} />
        <Route path="/pages/:slug*" component={DynamicPage} />
        <Route path="/privacy-policy" component={PrivacyPolicy} />
        <Route path="/terms-of-service" component={TermsOfService} />
        <Route path="/sitemap" component={SitemapPage} />
        <Route path="/404" component={NotFound} />
          <Route component={NotFound} />
</Switch>
    </Suspense>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          <FloatingPhone />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
